import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export type FeedbackType = "helpful" | "not_helpful" | "inaccurate";

interface TrailDescriptionFeedback {
  id: string;
  trail_id: string;
  description_id: string;
  user_id: string;
  feedback_type: FeedbackType;
  feedback_text: string;
  created_at: string;
  updated_at: string;
}

interface RegenerationResult {
  success?: boolean;
  flagged?: boolean;
  reason?: string;
  description?: any;
  regeneration_count?: number;
}

const NEGATIVE_FEEDBACK_THRESHOLD = 3;

export const useTrailDescriptionFeedback = (descriptionId: string | undefined) => {
  const [existingFeedback, setExistingFeedback] = useState<TrailDescriptionFeedback | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch existing feedback for this user + description
  useEffect(() => {
    if (!descriptionId || !user?.id) {
      setExistingFeedback(null);
      return;
    }

    const fetchExistingFeedback = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("trail_description_feedback")
          .select("*")
          .eq("description_id", descriptionId)
          .eq("user_id", user.id)
          .maybeSingle();

        if (!error && data) {
          setExistingFeedback(data as TrailDescriptionFeedback);
        } else {
          setExistingFeedback(null);
        }
      } catch (err) {
        console.error("Error fetching existing feedback:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExistingFeedback();
  }, [descriptionId, user?.id]);

  // Check if regeneration should be triggered and call the edge function
  const checkAndTriggerRegeneration = useCallback(async (
    trailId: string
  ): Promise<RegenerationResult | null> => {
    if (!descriptionId) return null;

    try {
      // Count negative feedback for this description
      const { count, error: countError } = await supabase
        .from("trail_description_feedback")
        .select("*", { count: "exact", head: true })
        .eq("description_id", descriptionId)
        .in("feedback_type", ["not_helpful", "inaccurate"]);

      if (countError) {
        console.error("Error counting feedback:", countError);
        return null;
      }

      // Check if threshold is met
      if ((count || 0) >= NEGATIVE_FEEDBACK_THRESHOLD) {
        setIsRegenerating(true);
        
        toast({
          title: "Generating improved description",
          description: "Based on community feedback, we're researching an improved description...",
        });

        const { data, error } = await supabase.functions.invoke(
          "regenerate-trail-description",
          {
            body: { trailId, descriptionId },
          }
        );

        if (error) {
          throw error;
        }

        // Handle flagged status (max regenerations reached)
        if (data?.flagged) {
          toast({
            title: "Description flagged for review",
            description: "This trail has limited online documentation. The description has been flagged for manual review.",
            variant: "default",
          });
          return data as RegenerationResult;
        }

        // Handle successful regeneration
        if (data?.success) {
          // Clear local existing feedback state (it was deleted server-side)
          setExistingFeedback(null);
          
          toast({
            title: "Description updated",
            description: "A new description has been generated based on community feedback.",
          });
          return data as RegenerationResult;
        }

        return data as RegenerationResult;
      }

      return null;
    } catch (err: any) {
      console.error("Error triggering regeneration:", err);
      toast({
        title: "Regeneration failed",
        description: err.message || "Failed to generate improved description",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsRegenerating(false);
    }
  }, [descriptionId, toast]);

  const submitFeedback = async (
    trailId: string,
    feedbackType: FeedbackType,
    feedbackText: string
  ): Promise<{ success: boolean; regenerationTriggered: boolean; regenerationResult?: RegenerationResult }> => {
    if (!descriptionId || !user?.id) {
      toast({
        title: "Sign in required",
        description: "Please sign in to submit feedback",
        variant: "destructive",
      });
      return { success: false, regenerationTriggered: false };
    }

    if (feedbackText.length < 10) {
      toast({
        title: "Feedback too short",
        description: "Please provide at least 10 characters of feedback",
        variant: "destructive",
      });
      return { success: false, regenerationTriggered: false };
    }

    setIsSubmitting(true);
    try {
      if (existingFeedback) {
        // Update existing feedback
        const { error } = await supabase
          .from("trail_description_feedback")
          .update({
            feedback_type: feedbackType,
            feedback_text: feedbackText,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingFeedback.id);

        if (error) throw error;

        setExistingFeedback({
          ...existingFeedback,
          feedback_type: feedbackType,
          feedback_text: feedbackText,
          updated_at: new Date().toISOString(),
        });

        toast({
          title: "Feedback updated",
          description: "Thank you for updating your feedback!",
        });
      } else {
        // Insert new feedback
        const { data, error } = await supabase
          .from("trail_description_feedback")
          .insert({
            trail_id: trailId,
            description_id: descriptionId,
            user_id: user.id,
            feedback_type: feedbackType,
            feedback_text: feedbackText,
          })
          .select()
          .single();

        if (error) throw error;

        setExistingFeedback(data as TrailDescriptionFeedback);

        toast({
          title: "Feedback submitted",
          description: "Thank you for your feedback!",
        });
      }

      // Check if regeneration should be triggered (only for negative feedback)
      if (feedbackType === "not_helpful" || feedbackType === "inaccurate") {
        const regenerationResult = await checkAndTriggerRegeneration(trailId);
        if (regenerationResult) {
          return { 
            success: true, 
            regenerationTriggered: true, 
            regenerationResult 
          };
        }
      }

      return { success: true, regenerationTriggered: false };
    } catch (err: any) {
      console.error("Error submitting feedback:", err);
      toast({
        title: "Failed to submit feedback",
        description: err.message || "Please try again later",
        variant: "destructive",
      });
      return { success: false, regenerationTriggered: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    existingFeedback,
    isLoading,
    isSubmitting,
    isRegenerating,
    submitFeedback,
    isAuthenticated: !!user,
  };
};
